import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { 
  DollarSign, TrendingUp, ShoppingBag, Calendar, Download, Printer, 
  Truck, ShoppingCart, UtensilsCrossed, BookOpen, Package, Clock,
  ArrowUpRight, ChevronDown, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export default function FinancialReports() {
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  const [reports, setReports] = useState(null);
  const [byOrderType, setByOrderType] = useState(null);
  const [orders, setOrders] = useState([]);
  const [tableBookings, setTableBookings] = useState([]);
  const [tableSessions, setTableSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: getDefaultStartDate(),
    end: getDefaultEndDate(),
  });
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const printRef = useRef(null);

  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split('T')[0];
  }

  const periods = [
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'year', label: 'This Year' },
    { id: 'custom', label: 'Custom' },
  ];

  const orderTypes = [
    { id: 'all', label: 'All Orders' },
    { id: 'delivery', label: 'Delivery' },
    { id: 'takeaway', label: 'Takeaway' },
    { id: 'dine_in', label: 'Dine-in' },
  ];

  const selectPeriod = (period) => {
    setSelectedPeriod(period);
    const today = new Date();
    let start, end;

    switch (period) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = weekStart.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      default:
        return;
    }

    setDateRange({ start, end });
  };

  useEffect(() => {
    if (selectedRestaurant) {
      fetchReports();
    }
  }, [selectedRestaurant, dateRange, orderTypeFilter]);

  const fetchReports = async () => {
    if (!selectedRestaurant) return;
    
    try {
      setLoading(true);
      const params = new URLSearchParams({
        restaurant_id: selectedRestaurant.id,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      
      if (orderTypeFilter !== 'all') {
        params.append('order_type_filter', orderTypeFilter);
      }

      const response = await api.get(`/reports/restaurant-financial?${params}`);
      setReports(response.data.summary);
      setByOrderType(response.data.by_order_type);
      setOrders(response.data.orders || []);
      setTableBookings(response.data.table_bookings || []);
      setTableSessions(response.data.table_sessions || []);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load financial reports');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!orders.length && !tableBookings.length) {
      toast.error('No data to export');
      return;
    }

    // Orders CSV
    const orderHeaders = ['Order ID', 'Date', 'Type', 'Customer', 'Items', 'Subtotal', 'Delivery Fee', 'Commission', 'Total', 'Status'];
    const orderRows = orders.map(order => [
      order.id?.slice(0, 8) || '',
      order.created_at?.split('T')[0] || '',
      order.order_type || 'delivery',
      order.customer_name || 'Guest',
      order.items?.length || 0,
      `£${(order.subtotal || 0).toFixed(2)}`,
      `£${(order.delivery_fee || 0).toFixed(2)}`,
      `£${(order.platform_commission || 0).toFixed(2)}`,
      `£${(order.total || 0).toFixed(2)}`,
      order.status || '',
    ]);

    // Table Bookings CSV
    const bookingHeaders = ['Booking ID', 'Date', 'Time', 'Party Size', 'Customer', 'Status', 'Deposit'];
    const bookingRows = tableBookings.map(booking => [
      booking.id?.slice(0, 8) || '',
      booking.date || '',
      booking.time || '',
      booking.party_size || 0,
      booking.customer_name || 'Guest',
      booking.status || '',
      `£${(booking.deposit_amount || 0).toFixed(2)}`,
    ]);

    let csvContent = 'ORDERS\n' + [orderHeaders.join(','), ...orderRows.map(r => r.join(','))].join('\n');
    csvContent += '\n\nTABLE BOOKINGS\n' + [bookingHeaders.join(','), ...bookingRows.map(r => r.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `financial_report_${dateRange.start}_${dateRange.end}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Financial Report - ${selectedRestaurant?.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1, h2 { color: #1e293b; }
            .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
            .summary-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; }
            .summary-card h3 { font-size: 14px; color: #64748b; margin: 0 0 8px 0; }
            .summary-card p { font-size: 24px; font-weight: bold; color: #1e293b; margin: 0; }
            .breakdown { margin: 24px 0; }
            .breakdown-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
            th { background: #f8fafc; font-weight: 600; }
            .text-right { text-align: right; }
            .period { color: #64748b; margin-bottom: 16px; }
            .section { margin-top: 32px; page-break-inside: avoid; }
          </style>
        </head>
        <body>
          <h1>Financial Report</h1>
          <p><strong>${selectedRestaurant?.name}</strong></p>
          <p class="period">Period: ${dateRange.start} to ${dateRange.end}</p>
          
          <div class="summary">
            <div class="summary-card">
              <h3>Total Revenue</h3>
              <p>£${reports?.total_revenue?.toFixed(2) || '0.00'}</p>
            </div>
            <div class="summary-card">
              <h3>Net Revenue</h3>
              <p>£${reports?.net_revenue?.toFixed(2) || '0.00'}</p>
            </div>
            <div class="summary-card">
              <h3>Platform Commission</h3>
              <p>£${reports?.platform_commission?.toFixed(2) || '0.00'}</p>
            </div>
            <div class="summary-card">
              <h3>Total Orders</h3>
              <p>${reports?.total_orders || 0}</p>
            </div>
          </div>

          <div class="section">
            <h2>Revenue by Order Type</h2>
            <div class="breakdown">
              <div class="breakdown-item">
                <span>Delivery Orders (${byOrderType?.delivery?.count || 0})</span>
                <span>£${byOrderType?.delivery?.revenue?.toFixed(2) || '0.00'}</span>
              </div>
              <div class="breakdown-item">
                <span>Takeaway Orders (${byOrderType?.takeaway?.count || 0})</span>
                <span>£${byOrderType?.takeaway?.revenue?.toFixed(2) || '0.00'}</span>
              </div>
              <div class="breakdown-item">
                <span>Dine-in Orders (${byOrderType?.dine_in?.count || 0})</span>
                <span>£${byOrderType?.dine_in?.revenue?.toFixed(2) || '0.00'}</span>
              </div>
              <div class="breakdown-item">
                <span>Table Bookings (${byOrderType?.table_bookings?.count || 0})</span>
                <span>Deposits: £${byOrderType?.table_bookings?.deposits_collected?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>

          <div class="section">
            <h2>Recent Orders</h2>
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th class="text-right">Total</th>
                  <th class="text-right">Commission</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                ${orders.slice(0, 20).map(order => `
                  <tr>
                    <td>${order.id?.slice(0, 8) || ''}</td>
                    <td>${order.created_at?.split('T')[0] || ''}</td>
                    <td>${order.order_type || 'delivery'}</td>
                    <td class="text-right">£${(order.total || 0).toFixed(2)}</td>
                    <td class="text-right">£${(order.platform_commission || 0).toFixed(2)}</td>
                    <td>${order.status || ''}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <p style="margin-top: 24px; color: #64748b; font-size: 12px;">
            Generated on ${new Date().toLocaleString()}
          </p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-slate-900">Financial Reports</h1>
        <div className="grid md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-1/2 mb-3"></div>
              <div className="h-8 bg-slate-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" ref={printRef}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Financial Reports</h1>
          <p className="text-sm text-slate-500 mt-1">
            Track all revenue sources: delivery, takeaway, dine-in & bookings
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-report-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Time Period */}
            <div className="flex gap-1">
              {periods.map((period) => (
                <Button
                  key={period.id}
                  variant={selectedPeriod === period.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => selectPeriod(period.id)}
                  className={selectedPeriod === period.id ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  data-testid={`period-${period.id}`}
                >
                  {period.label}
                </Button>
              ))}
            </div>
            
            {/* Order Type Filter */}
            <div className="flex items-center gap-2 border-l pl-4">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={orderTypeFilter}
                onChange={(e) => setOrderTypeFilter(e.target.value)}
                className="text-sm border rounded px-2 py-1"
                data-testid="order-type-filter"
              >
                {orderTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>
            
            {selectedPeriod === 'custom' && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">From:</Label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="w-36"
                    data-testid="date-from"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm">To:</Label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="w-36"
                    data-testid="date-to"
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-revenue">
                  £{reports?.total_revenue?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Net Revenue</p>
                <p className="text-2xl font-bold text-green-600" data-testid="net-revenue">
                  £{reports?.net_revenue?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-slate-400">After commission</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Platform Commission</p>
                <p className="text-2xl font-bold text-orange-500" data-testid="platform-commission">
                  £{reports?.platform_commission?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-slate-400">Delivery orders only</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center">
                <Package className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Orders</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-orders">
                  {reports?.total_orders || 0}
                </p>
                <p className="text-xs text-slate-400">
                  Avg: £{reports?.average_order_value?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Breakdown by Order Type */}
      <div className="grid md:grid-cols-4 gap-4">
        {/* Delivery */}
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Delivery</p>
                <p className="text-xs text-slate-500">{byOrderType?.delivery?.count || 0} orders</p>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Revenue</span>
                <span className="text-sm font-medium">£{byOrderType?.delivery?.revenue?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Commission</span>
                <span className="text-sm text-orange-600">-£{byOrderType?.delivery?.commission?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between border-t pt-1">
                <span className="text-sm font-medium text-slate-700">Net</span>
                <span className="text-sm font-bold text-green-600">£{byOrderType?.delivery?.net_revenue?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Takeaway */}
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Takeaway</p>
                <p className="text-xs text-slate-500">{byOrderType?.takeaway?.count || 0} orders</p>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Revenue</span>
                <span className="text-sm font-medium">£{byOrderType?.takeaway?.revenue?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Commission</span>
                <span className="text-sm text-green-600">£0.00 (No fee)</span>
              </div>
              <div className="flex justify-between border-t pt-1">
                <span className="text-sm font-medium text-slate-700">Net</span>
                <span className="text-sm font-bold text-green-600">£{byOrderType?.takeaway?.net_revenue?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dine-in */}
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <UtensilsCrossed className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Dine-in</p>
                <p className="text-xs text-slate-500">{byOrderType?.dine_in?.count || 0} orders</p>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Revenue</span>
                <span className="text-sm font-medium">£{byOrderType?.dine_in?.revenue?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Commission</span>
                <span className="text-sm text-green-600">£0.00 (No fee)</span>
              </div>
              <div className="flex justify-between border-t pt-1">
                <span className="text-sm font-medium text-slate-700">Net</span>
                <span className="text-sm font-bold text-green-600">£{byOrderType?.dine_in?.net_revenue?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Bookings */}
        <Card className="border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Table Bookings</p>
                <p className="text-xs text-slate-500">{byOrderType?.table_bookings?.total_bookings || 0} bookings</p>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Confirmed</span>
                <span className="text-sm font-medium">{byOrderType?.table_bookings?.confirmed || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Completed</span>
                <span className="text-sm font-medium">{byOrderType?.table_bookings?.completed || 0}</span>
              </div>
              <div className="flex justify-between border-t pt-1">
                <span className="text-sm font-medium text-slate-700">Deposits</span>
                <span className="text-sm font-bold text-teal-600">£{byOrderType?.table_bookings?.deposits_collected?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Completed Orders</p>
                <p className="text-xl font-bold text-green-600">{reports?.completed_orders || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending Orders</p>
                <p className="text-xl font-bold text-yellow-600">{reports?.pending_orders || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Truck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Delivery Fees Collected</p>
                <p className="text-xl font-bold text-blue-600">£{reports?.delivery_fees?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation for Tables */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex gap-4">
            <Button
              variant={activeTab === 'overview' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('overview')}
              className={activeTab === 'overview' ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Orders ({orders.length})
            </Button>
            <Button
              variant={activeTab === 'bookings' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('bookings')}
              className={activeTab === 'bookings' ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Table Bookings ({tableBookings.length})
            </Button>
            <Button
              variant={activeTab === 'sessions' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveTab('sessions')}
              className={activeTab === 'sessions' ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              <UtensilsCrossed className="w-4 h-4 mr-2" />
              Dine-in Sessions ({tableSessions.length})
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Orders Table */}
          {activeTab === 'overview' && (
            orders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Order ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Customer</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Subtotal</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Delivery</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Commission</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Total</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 font-mono text-sm">{order.id?.slice(0, 8)}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.order_type === 'delivery' ? 'bg-blue-100 text-blue-700' :
                            order.order_type === 'takeaway' ? 'bg-purple-100 text-purple-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {order.order_type || 'delivery'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm">{order.customer_name || 'Guest'}</td>
                        <td className="py-3 px-4 text-sm text-right">£{(order.subtotal || 0).toFixed(2)}</td>
                        <td className="py-3 px-4 text-sm text-right">£{(order.delivery_fee || 0).toFixed(2)}</td>
                        <td className="py-3 px-4 text-sm text-right text-orange-600">
                          {order.platform_commission > 0 ? `-£${(order.platform_commission || 0).toFixed(2)}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-right font-medium">
                          £{(order.total || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.status === 'delivered' || order.status === 'completed' ? 'bg-green-100 text-green-700' :
                            order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <ShoppingBag className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No orders found for the selected period</p>
              </div>
            )
          )}

          {/* Table Bookings */}
          {activeTab === 'bookings' && (
            tableBookings.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Booking ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Time</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Party Size</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Customer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Contact</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Deposit</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableBookings.map((booking) => (
                      <tr key={booking.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 font-mono text-sm">{booking.id?.slice(0, 8)}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{booking.date}</td>
                        <td className="py-3 px-4 text-sm">{booking.time}</td>
                        <td className="py-3 px-4 text-sm text-center">{booking.party_size}</td>
                        <td className="py-3 px-4 text-sm">{booking.customer_name || 'Guest'}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{booking.customer_phone || '-'}</td>
                        <td className="py-3 px-4 text-sm text-right">
                          {booking.deposit_paid ? `£${(booking.deposit_amount || 0).toFixed(2)}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {booking.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No table bookings found for the selected period</p>
              </div>
            )
          )}

          {/* Dine-in Sessions */}
          {activeTab === 'sessions' && (
            tableSessions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Session ID</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Table</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Party Size</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Items</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Total</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableSessions.map((session) => (
                      <tr key={session.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 font-mono text-sm">{session.id?.slice(0, 8)}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {session.created_at ? new Date(session.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3 px-4 text-sm">{session.table_number || '-'}</td>
                        <td className="py-3 px-4 text-sm text-center">{session.party_size || '-'}</td>
                        <td className="py-3 px-4 text-sm text-right">{session.items?.length || 0}</td>
                        <td className="py-3 px-4 text-sm text-right font-medium">
                          £{(session.total || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            session.status === 'completed' || session.status === 'bill_paid' ? 'bg-green-100 text-green-700' :
                            session.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {session.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <UtensilsCrossed className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No dine-in sessions found for the selected period</p>
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
